import { Stack } from '@mui/material';
import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import RelatedRecordsTable from '@components/RelatedRecordsTable';

import { useGetLabDiagnosticReports } from '@hooks/api/diagnostic-report';
import { useSession } from '@hooks/useSession';
import { useViewable } from '@hooks/useViewable';

import { LabResultsGrid } from './LabResultsGrid';

const messages = defineMessages({
  lastResults: {
    id: 'labResultsDrilldown.lastResults',
    defaultMessage: 'Last Results',
  },
});

interface LabResultsDrilldownProps {
  type?: string;
  title: string | null;
  /** Only needed for shared resource context */
  patient?: string;
}

const LabResultsDrilldown: React.FC<LabResultsDrilldownProps> = ({
  type = '',
  title = '',
  patient,
}) => {
  const { formatMessage } = useIntl();
  const {
    state: { isAuthenticated },
  } = useSession();
  const { data, loading } = useGetLabDiagnosticReports({
    patient,
  });
  const filteredData = data.data.filter(({ code }) => code === type);

  useViewable(filteredData);

  return (
    <ContentLoader loading={loading}>
      <Stack sx={{ width: '100%' }} spacing={2}>
        {filteredData.map((response) => {
          const { id } = response;
          const testDate = response.getTestDateStr();
          return (
            <React.Fragment key={id}>
              <LabResultsGrid
                group={type}
                key={id}
                response={response}
                subtitle={
                  testDate
                    ? `${formatMessage(
                        messages.lastResults
                      )}: ${response.getTestDateStr()}`
                    : ''
                }
                title={title}
                type={type}
              />
              {isAuthenticated && (
                <RelatedRecordsTable records={response.associatedRecords} />
              )}
            </React.Fragment>
          );
        })}
      </Stack>
    </ContentLoader>
  );
};

export default withErrorBoundary(LabResultsDrilldown, {
  FallbackComponent: ErrorBoundaryFallback,
});
